import type { HttpClient } from './http';

export interface McpServerConfig {
  id: number;
  name: string;
  url: string;
  enabled: boolean;
  connectionStatus: 'disconnected' | 'authorizing' | 'connected';
}

export interface McpToolInfo {
  serverConfigId: number;
  originalToolName: string;
  description: string;
}

export interface TestConnectionResult {
  success: boolean;
  serverInfo?: { name: string; version: string };
  toolCount?: number;
  error?: string;
  needsAuthorization?: boolean;
}

export interface McpOAuthHints {
  instanceId?: string | null;
  profileId?: string | null;
}

function toAbsoluteApiUrl(baseUrl: string, path: string): string {
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  if (/^https?:\/\//.test(normalizedBase)) {
    return new URL(path, normalizedBase).toString();
  }

  const origin = globalThis.window?.location?.origin ?? 'http://localhost';
  const basePath = normalizedBase.startsWith('/')
    ? normalizedBase
    : `/${normalizedBase}`;
  return new URL(`${basePath}${path}`, origin).toString();
}

export function createMcpServersApi(http: HttpClient, baseUrl: string) {
  const base = 'users/me/mcp';
  return {
    listServers: () => http.get<McpServerConfig[]>(`${base}/servers`),
    addServer: (data: { name: string; url: string }) =>
      http.post<McpServerConfig>(`${base}/servers`, data),
    addKlardatenServer: (data: { name: string }) =>
      http.post<McpServerConfig>(`${base}/servers/klardaten`, data),
    updateServer: (
      id: number,
      data: { name?: string; url?: string; enabled?: boolean }
    ) => http.put<McpServerConfig>(`${base}/servers/${id}`, data),
    deleteServer: (id: number) => http.delete<void>(`${base}/servers/${id}`),
    testSavedServer: (id: number) =>
      http.post<TestConnectionResult>(`${base}/servers/${id}/test-connection`),
    listTools: () => http.get<McpToolInfo[]>(`${base}/tools`),
    getOAuthStartUrl: (id: number, hints?: McpOAuthHints) => {
      const url = new URL(
        toAbsoluteApiUrl(baseUrl, `${base}/servers/${id}/oauth/start`)
      );
      if (hints?.instanceId) {
        url.searchParams.set('instance_id', hints.instanceId);
      }
      if (hints?.profileId) {
        url.searchParams.set('profile_id', hints.profileId);
      }
      return url.toString();
    },
  };
}

export type McpServersApi = ReturnType<typeof createMcpServersApi>;
